import type { EmployeeExecutionBlocker } from 'src/api/generated';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import {
  getCapabilityDisplayName,
  getToolMetadata,
} from './employee-tool-presentation.util';

const CATEGORY_TRANSLATION_KEYS: Record<
  EmployeeExecutionBlocker['category'],
  TranslationKey
> = {
  'missing-capability':
    'automations.aiAgents.assistance.category.missingCapability',
  'configuration-unavailable':
    'automations.aiAgents.assistance.category.configurationUnavailable',
  'terminal-tool-failure':
    'automations.aiAgents.assistance.category.terminalToolFailure',
  'repeated-tool-failure':
    'automations.aiAgents.assistance.category.repeatedToolFailure',
  'terminal-operation-failure':
    'automations.aiAgents.assistance.category.terminalOperationFailure',
};

const REASON_TRANSLATION_KEYS: Record<
  EmployeeExecutionBlocker['category'],
  TranslationKey
> = {
  'missing-capability':
    'automations.aiAgents.assistance.reason.missingCapability',
  'configuration-unavailable':
    'automations.aiAgents.assistance.reason.configurationUnavailable',
  'terminal-tool-failure':
    'automations.aiAgents.assistance.reason.terminalToolFailure',
  'repeated-tool-failure':
    'automations.aiAgents.assistance.reason.repeatedToolFailure',
  'terminal-operation-failure':
    'automations.aiAgents.assistance.reason.terminalOperationFailure',
};

const getRequiredToolNames = (
  blocker: EmployeeExecutionBlocker,
  translate: I18nContextValue['t']
): string[] => {
  if (
    !('requiredAnyOfToolNames' in blocker) ||
    !Array.isArray(blocker.requiredAnyOfToolNames)
  ) {
    return [];
  }

  return [
    ...new Set(
      blocker.requiredAnyOfToolNames.map(
        (toolName) => getToolMetadata(toolName, translate).displayName
      )
    ),
  ];
};

/**
 * Builds user-facing blocker copy without exposing internal runtime messages.
 *
 * @param blocker - Structured execution blocker persisted by Automations.
 * @param translate - Tenant-localized translation function.
 * @returns Localized category, reason, and context labels for the blocker.
 */
export const getEmployeeBlockerPresentation = (
  blocker: EmployeeExecutionBlocker,
  translate: I18nContextValue['t']
) => {
  const categoryLabel = translate(CATEGORY_TRANSLATION_KEYS[blocker.category]);
  const reason = translate(REASON_TRANSLATION_KEYS[blocker.category]);

  if ('capability' in blocker && typeof blocker.capability === 'string') {
    const requiredToolNames = getRequiredToolNames(blocker, translate);
    const context =
      requiredToolNames.length === 1
        ? translate('automations.aiAgents.assistance.requiredTool', {
            toolName: requiredToolNames[0],
          })
        : requiredToolNames.length > 1
          ? translate('automations.aiAgents.assistance.requiredAnyTool', {
              toolNames: requiredToolNames.join(' · '),
            })
          : translate('automations.aiAgents.assistance.capability', {
              capability: getCapabilityDisplayName(
                blocker.capability,
                translate
              ),
            });

    return {
      categoryLabel,
      reason,
      context,
    };
  }

  if (!('toolName' in blocker) || typeof blocker.toolName !== 'string') {
    return {
      categoryLabel,
      reason,
      context: categoryLabel,
    };
  }

  const toolName = getToolMetadata(blocker.toolName, translate).displayName;
  if ('attempts' in blocker && typeof blocker.attempts === 'number') {
    return {
      categoryLabel,
      reason,
      context: translate('automations.aiAgents.assistance.toolAttempts', {
        toolName,
        attempts: blocker.attempts,
      }),
    };
  }

  return {
    categoryLabel,
    reason,
    context: translate('automations.aiAgents.assistance.affectedTool', {
      toolName,
    }),
  };
};
